import { useId, useState } from "react";
import {
  Box,
  Button,
  Container,
  Dialog,
  Flex,
  Heading,
  HStack,
  Icon,
  IconButton,
  Link,
  Portal,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react";
import {
  FaGithub,
  FaBars,
  FaTimes,
} from "react-icons/fa";
import { AdSlot } from "./AdSlot";

/**
 * Diagonal cyan line field matching the brand still.
 * Half-screen composition; each stroke owns independent color flow.
 * 16:9 artboard + xMidYMax slice pins strokes to the container bottom.
 */
function BrandLineField({ opacity = 1 }: { opacity?: number }) {
  const uid = useId().replace(/:/g, "");
  const W = 1280;
  const H = 720;
  const intercepts = [80, 160, 240, 320, 400, 480, 560, 640, 720];
  const slope = 0.38;

  const palettes: [string, string, string][] = [
    ["#042a32", "#0a5c66", "#0e8a92"],
    ["#053038", "#0c6a72", "#12a0a6"],
    ["#063840", "#0d7a82", "#18b8b4"],
    ["#074248", "#0f8c90", "#22ccc4"],
    ["#084a50", "#12a09a", "#2ed8ce"],
    ["#095258", "#14b0a8", "#3ee4d8"],
    ["#0a5a60", "#00c4bc", "#4af0e4"],
    ["#0b6268", "#00d4cc", "#5ef5ec"],
    ["#0c6a70", "#14e0d8", "#7afff6"],
  ];

  const x1 = -200;
  const x2 = W + 200;

  return (
    <div
      aria-hidden
      style={{
        position: "absolute",
        inset: 0,
        overflow: "hidden",
        opacity,
        pointerEvents: "none",
        zIndex: 0,
      }}
    >
      <svg
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMax slice"
        width="100%"
        height="100%"
        style={{ display: "block", width: "100%", height: "100%" }}
      >
        <defs>
          {intercepts.map((y0, i) => {
            const y1 = y0 + (x1 - 0) * slope;
            const y2 = y0 + (x2 - 0) * slope;
            const [deep, mid, bright] = palettes[i];
            const dur = `${9 + i * 1.7}s`;
            const delay = `${-i * 1.3}s`;
            return (
              <linearGradient
                key={`g-${y0}`}
                id={`${uid}-line-${i}`}
                gradientUnits="userSpaceOnUse"
                x1={x1}
                y1={y1}
                x2={x2}
                y2={y2}
              >
                <stop offset="0%" stopColor={deep}>
                  <animate
                    attributeName="stop-color"
                    values={`${deep};${mid};${deep}`}
                    dur={dur}
                    begin={delay}
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="offset"
                    values="-0.2;0.4;1.2"
                    dur={dur}
                    begin={delay}
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="50%" stopColor={mid}>
                  <animate
                    attributeName="stop-color"
                    values={`${mid};${bright};${mid}`}
                    dur={dur}
                    begin={delay}
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="offset"
                    values="0.15;0.55;0.95"
                    dur={dur}
                    begin={delay}
                    repeatCount="indefinite"
                  />
                </stop>
                <stop offset="100%" stopColor={bright}>
                  <animate
                    attributeName="stop-color"
                    values={`${bright};${mid};${bright}`}
                    dur={dur}
                    begin={delay}
                    repeatCount="indefinite"
                  />
                  <animate
                    attributeName="offset"
                    values="0.5;1.1;1.8"
                    dur={dur}
                    begin={delay}
                    repeatCount="indefinite"
                  />
                </stop>
              </linearGradient>
            );
          })}
        </defs>
        {intercepts.map((y0, i) => {
          const y1 = y0 + (x1 - 0) * slope;
          const y2 = y0 + (x2 - 0) * slope;
          const strokeWidth = 2.8 + (i % 3) * 0.45;
          return (
            <line
              key={y0}
              x1={x1}
              y1={y1}
              x2={x2}
              y2={y2}
              stroke={`url(#${uid}-line-${i})`}
              strokeWidth={strokeWidth}
              strokeLinecap="butt"
            />
          );
        })}
      </svg>
    </div>
  );
}

const VOID = "#000000";
const PEARL = "#f2f0ec";
const PEARL_MUTED = "#8b949e";
const CYAN = "#00d4cc";
const CYAN_MID = "#14b8b0";
const LINE = "rgba(255, 255, 255, 0.07)";

const PRIMARY_BTN = {
  bg: CYAN,
  color: VOID,
  rounded: "full" as const,
  fontWeight: "semibold" as const,
  fontFamily: "var(--font-display)",
  letterSpacing: "-0.01em",
  _hover: { bg: CYAN_MID },
};

const GLASS_BTN = {
  rounded: "full" as const,
  variant: "outline" as const,
  borderColor: "rgba(255, 255, 255, 0.14)",
  color: PEARL,
  bg: "rgba(0, 0, 0, 0.4)",
  backdropFilter: "blur(12px)",
  fontFamily: "var(--font-display)",
  fontWeight: "medium" as const,
  _hover: { bg: "rgba(0, 212, 204, 0.08)", borderColor: CYAN },
};

const NAV_LINKS = [
  { label: "Pricing", href: "#pricing" },
  { label: "Docs", href: "https://terella.dev/docs/getting-started.html", external: true },
];

const FEATURES = [
  {
    title: "One runtime, many agents",
    text: "Switch between Claude, Gemini, Codex, Copilot, Cline, Cursor, and Junie without rebuilding your workflow.",
  },
  {
    title: "Local-first state",
    text: "Workflows live in your repo. Agents work from your codebase, permissions, and docs.",
  },
  {
    title: "Desktop, CLI, Web, CI",
    text: "Run as a desktop app, terminal command, web console, or GitHub Actions pipeline.",
  },
];

function TerellaMark({ fontSize = "lg" }: { fontSize?: string }) {
  return (
    <Text as="span" className="terella-mark" fontSize={fontSize} lineHeight="1">
      terella
    </Text>
  );
}

function SectionLabel({ children }: { children: string }) {
  return (
    <HStack gap={3} mb={3}>
      <Box w={6} h="1px" bg={CYAN} flexShrink={0} />
      <Text
        fontSize="xs"
        fontWeight="medium"
        letterSpacing="0.14em"
        textTransform="uppercase"
        color={CYAN}
        fontFamily="var(--font-display)"
      >
        {children}
      </Text>
    </HStack>
  );
}

export function App() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [contactSalesOpen, setContactSalesOpen] = useState(false);
  const openContactSales = () => setContactSalesOpen(true);

  return (
    <Box bg={VOID} color={PEARL} fontFamily="var(--font-body)">
      {/* First screen: nav + hero fill the window */}
      <Box minH="100dvh" display="flex" flexDirection="column" position="relative">
        {/* Line field behind nav + hero so strokes aren't clipped by the app bar */}
        <BrandLineField />

        <Box
          as="header"
          px={4}
          borderBottomWidth="1px"
          borderBottomColor={LINE}
          bg="transparent"
          position="sticky"
          top={0}
          zIndex={50}
          flexShrink={0}
        >
          <Container maxW="3xl">
            <Flex h={14} alignItems="center" justifyContent="space-between">
              <Link href="/" _hover={{ textDecoration: "none" }} aria-label="Terella home">
                <TerellaMark fontSize="xl" />
              </Link>

              <HStack gap={6} display={{ base: "none", md: "flex" }}>
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    color={PEARL_MUTED}
                    _hover={{ color: PEARL, textDecoration: "none" }}
                    fontSize="sm"
                    fontFamily="var(--font-display)"
                    {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  href="https://github.com/terella-project"
                  color={PEARL_MUTED}
                  _hover={{ color: PEARL }}
                  aria-label="GitHub"
                >
                  <Icon as={FaGithub} fontSize="md" />
                </Link>
                <Button size="sm" {...PRIMARY_BTN} onClick={openContactSales}>
                  Contact Sales
                </Button>
              </HStack>

              <IconButton
                display={{ base: "flex", md: "none" }}
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
                variant="ghost"
                color={PEARL_MUTED}
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                <Icon as={mobileOpen ? FaTimes : FaBars} />
              </IconButton>
            </Flex>

            {mobileOpen && (
              <VStack align="stretch" pb={4} gap={1} display={{ md: "none" }}>
                {NAV_LINKS.map((link) => (
                  <Link
                    key={link.label}
                    href={link.href}
                    py={2}
                    color={PEARL_MUTED}
                    _hover={{ color: PEARL, textDecoration: "none" }}
                    onClick={() => setMobileOpen(false)}
                    {...(link.external ? { target: "_blank", rel: "noopener noreferrer" } : {})}
                  >
                    {link.label}
                  </Link>
                ))}
                <Link
                  href="https://github.com/terella-project"
                  py={2}
                  color={PEARL_MUTED}
                  _hover={{ color: PEARL, textDecoration: "none" }}
                >
                  GitHub
                </Link>
                <Button mt={2} {...PRIMARY_BTN} onClick={openContactSales}>
                  Contact Sales
                </Button>
              </VStack>
            )}
          </Container>
        </Box>

        <Box
          as="main"
          position="relative"
          flex="1"
          minH={0}
          display="flex"
          alignItems="center"
          overflow="hidden"
          borderBottomWidth="1px"
          borderBottomColor={LINE}
          bg="transparent"
          zIndex={1}
        >
          {/* Wordmark centered under nav CTA */}
          <Box
            position="absolute"
            top={{ base: 5, md: 8 }}
            left={0}
            right={0}
            px={4}
            zIndex={1}
            pointerEvents="none"
            display={{ base: "none", sm: "block" }}
            opacity={0.95}
          >
            <Container maxW="3xl">
              <Flex justify="flex-end">
                <Box
                  position="relative"
                  w="6.5rem"
                  h={{ base: "3.5rem", md: "5.5rem", lg: "6.5rem" }}
                >
                  <Text
                    className="terella-mark"
                    position="absolute"
                    top={0}
                    left="50%"
                    transform="translateX(-50%)"
                    whiteSpace="nowrap"
                    fontSize={{ base: "3.5rem", md: "5.5rem", lg: "6.5rem" }}
                    lineHeight="1"
                    fontWeight="600"
                    letterSpacing="-0.04em"
                    userSelect="none"
                  >
                    terella
                  </Text>
                </Box>
              </Flex>
            </Container>
          </Box>

          <Text
            position="absolute"
            bottom={{ base: 3, md: 4 }}
            left={{ base: 4, md: 6 }}
            zIndex={1}
            fontSize="xs"
            color="gray.600"
            letterSpacing="0.04em"
            fontFamily="var(--font-display)"
            userSelect="none"
            pointerEvents="none"
          >
            © 2026 terella
          </Text>

          <Container maxW="3xl" position="relative" zIndex={2} py={{ base: 20, md: 28 }}>
            <VStack
              gap={8}
              textAlign={{ base: "center", md: "left" }}
              align={{ base: "center", md: "flex-start" }}
              maxW={{ md: "30rem" }}
            >
              <VStack gap={5} align={{ base: "center", md: "flex-start" }} className="hero-rise">
                <Heading
                  as="h1"
                  className="font-display"
                  fontWeight="600"
                  fontSize={{ base: "4xl", md: "5xl" }}
                  lineHeight="1.02"
                  letterSpacing="-0.045em"
                >
                  Long horizon agents
                </Heading>
                <Text
                  color={PEARL_MUTED}
                  fontSize={{ base: "md", md: "lg" }}
                  lineHeight="1.65"
                  maxW="28rem"
                >
                  An open-source runtime that turns Claude, Codex, Gemini, Copilot, and more into
                  repeatable workflows across Desktop, CLI, Web, and CI.
                </Text>
              </VStack>

              <Stack
                direction={{ base: "column", sm: "row" }}
                gap={3}
                justify={{ base: "center", md: "flex-start" }}
                className="hero-rise hero-rise-delay-1"
              >
                <Button size="lg" px={8} {...PRIMARY_BTN} onClick={openContactSales}>
                  Contact Sales
                </Button>
                <Button
                  size="lg"
                  px={8}
                  {...GLASS_BTN}
                  as="a"
                  href="https://github.com/terella-project"
                >
                  <Icon as={FaGithub} mr={2} />
                  GitHub
                </Button>
              </Stack>

            </VStack>
          </Container>
        </Box>
      </Box>

      {/* Lower page */}
      <Box position="relative" overflow="hidden" bg={VOID}>
        <BrandLineField opacity={0.22} />

        <Box py={{ base: 20, md: 24 }} position="relative" zIndex={1} bg="transparent">
          <Container maxW="3xl">
            <SectionLabel>Capabilities</SectionLabel>
            <VStack gap={0} align="stretch" mt={6}>
              {FEATURES.map((item, i) => (
                <Box
                  key={item.title}
                  className="feature-row"
                  py={6}
                  borderBottomWidth={i < FEATURES.length - 1 ? "1px" : 0}
                  borderBottomColor={LINE}
                >
                  <Text
                    className="font-display"
                    fontWeight="600"
                    fontSize="lg"
                    mb={2}
                    color={PEARL}
                    letterSpacing="-0.02em"
                  >
                    {item.title}
                  </Text>
                  <Text color="gray.500" fontSize="md" lineHeight="1.7" maxW="34rem">
                    {item.text}
                  </Text>
                </Box>
              ))}
            </VStack>
          </Container>
        </Box>

        <Box
          id="pricing"
          py={{ base: 20, md: 24 }}
          borderTopWidth="1px"
          borderTopColor={LINE}
          scrollMarginTop="4rem"
          position="relative"
          zIndex={1}
          bg="transparent"
        >
          <Container maxW="3xl">
            <VStack gap={10} align="stretch">
              <Box>
                <SectionLabel>Pricing</SectionLabel>
                <Heading
                  as="h2"
                  className="font-display"
                  size="lg"
                  fontWeight="600"
                  letterSpacing="-0.03em"
                  mb={2}
                  mt={1}
                >
                  Free until scale demands fairness
                </Heading>
                <Text color="gray.500" fontSize="md" lineHeight="1.65" maxW="32rem">
                  Free for individuals and small teams. Organizations over $500k/year share a portion
                  of the value created.
                </Text>
              </Box>

              <Stack direction={{ base: "column", sm: "row" }} gap={5} align="stretch">
                <Box flex={1} p={7} rounded="2xl" className="glass price-card">
                  <Text
                    fontSize="xs"
                    color={CYAN}
                    mb={2}
                    fontWeight="medium"
                    letterSpacing="0.12em"
                    textTransform="uppercase"
                    fontFamily="var(--font-display)"
                  >
                    Community
                  </Text>
                  <Text
                    className="font-display"
                    fontSize="3xl"
                    fontWeight="600"
                    mb={2}
                    letterSpacing="-0.03em"
                  >
                    Free
                  </Text>
                  <Text color="gray.500" fontSize="sm" mb={8} lineHeight="1.65">
                    All adapters, local workflows, CLI &amp; desktop. Revenue under $500k/year.
                  </Text>
                  <Button w="full" size="md" {...PRIMARY_BTN} onClick={openContactSales}>
                    Contact Sales
                  </Button>
                </Box>

                <Box flex={1} p={7} rounded="2xl" className="glass price-card">
                  <Text
                    fontSize="xs"
                    color="gray.500"
                    mb={2}
                    fontWeight="medium"
                    letterSpacing="0.12em"
                    textTransform="uppercase"
                    fontFamily="var(--font-display)"
                  >
                    Enterprise
                  </Text>
                  <Text
                    className="font-display"
                    fontSize="3xl"
                    fontWeight="600"
                    mb={2}
                    letterSpacing="-0.03em"
                  >
                    Fair share
                  </Text>
                  <Text color="gray.500" fontSize="sm" mb={8} lineHeight="1.65">
                    Everything in Community plus revenue-sharing for orgs over $500k/year.
                  </Text>
                  <Button
                    w="full"
                    size="md"
                    {...GLASS_BTN}
                    onClick={openContactSales}
                  >
                    Contact Sales
                  </Button>
                </Box>
              </Stack>

              <Text color="gray.600" fontSize="sm">
                See the{" "}
                <Link
                  href="https://github.com/terella-project/terella/blob/master/REVENUE-SHARING.md"
                  color={CYAN}
                  _hover={{ color: CYAN_MID }}
                >
                  revenue sharing agreement
                </Link>{" "}
                for details.
              </Text>
            </VStack>
          </Container>
        </Box>

        <AdSlot />

        <Box
          as="footer"
          py={10}
          borderTopWidth="1px"
          borderTopColor={LINE}
          position="relative"
          zIndex={1}
          bg="transparent"
        >
          <Container maxW="3xl">
            <Flex
              direction={{ base: "column", sm: "row" }}
              justify="space-between"
              align="center"
              gap={4}
            >
              <HStack gap={3}>
                <TerellaMark fontSize="md" />
                <Text color="gray.600" fontSize="sm" fontFamily="var(--font-display)">
                  © 2026
                </Text>
              </HStack>
              <HStack gap={5}>
                <Link
                  href="https://github.com/terella-project"
                  color="gray.600"
                  fontSize="sm"
                  fontFamily="var(--font-display)"
                  _hover={{ color: CYAN }}
                >
                  GitHub
                </Link>
                <Link
                  href="https://terella.dev/docs/getting-started.html"
                  color="gray.600"
                  fontSize="sm"
                  fontFamily="var(--font-display)"
                  _hover={{ color: CYAN }}
                >
                  Docs
                </Link>
                <Link
                  href="/privacy"
                  color="gray.600"
                  fontSize="sm"
                  fontFamily="var(--font-display)"
                  _hover={{ color: CYAN }}
                >
                  Privacy
                </Link>
              </HStack>
            </Flex>
          </Container>
        </Box>
      </Box>

      <Dialog.Root
        open={contactSalesOpen}
        onOpenChange={({ open }) => setContactSalesOpen(open)}
        placement="center"
      >
        <Portal>
          <Dialog.Backdrop bg="rgba(0, 0, 0, 0.8)" />
          <Dialog.Positioner p={4}>
            <Dialog.Content
              bg="#0b1214"
              borderWidth="1px"
              borderColor="rgba(0, 212, 204, 0.25)"
              rounded="2xl"
              maxW="md"
              p={7}
            >
              <Dialog.Header p={0} mb={3}>
                <Dialog.Title
                  className="font-display"
                  fontSize="2xl"
                  fontWeight="600"
                  letterSpacing="-0.03em"
                >
                  Contact Sales
                </Dialog.Title>
              </Dialog.Header>
              <Dialog.Body p={0}>
                <Text color={PEARL_MUTED} lineHeight="1.65" mb={5}>
                  Download access is currently available through a guided conversation. Reach us at
                  <Link
                    href="mailto:contact@terella.dev"
                    color={CYAN}
                    ml={1}
                    _hover={{ color: CYAN_MID }}
                  >
                    contact@terella.dev
                  </Link>
                  .
                </Text>
              </Dialog.Body>
              <Dialog.CloseTrigger
                aria-label="Close contact sales dialog"
                position="absolute"
                top={3}
                right={3}
                p={2}
                rounded="full"
                color={PEARL_MUTED}
                _hover={{ bg: "rgba(255, 255, 255, 0.08)", color: PEARL }}
              >
                <Icon as={FaTimes} />
              </Dialog.CloseTrigger>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </Box>
  );
}

export default App;
